/**
 * DATA LAYER (DL): SECUENCIA (SIMON SAYS MULTIPLAYER)
 * Módulo de comunicación REST nativa (Fetch API) con el backend de Interaktia-V2 para la dinámica Secuencia.
 * Gestiona el ciclo de vida de la partida, registro de toques en tiempo real, finalización de playback,
 * control de portada y proyección en pantalla gigante, histórico de partidas, CRUD de temas visuales,
 * configuración de pantalla/velocidad y subida optimizada de recursos multimedia.
 * 
 * @module utilities/DL/Sequence
 */

/**
 * Resuelve y retorna la URL base para los endpoints del controlador Sequence.
 * Compatible con rutas estándar de API en Interaktia-V2 ('/api/v1/Sequence' o '/api/Sequence').
 * 
 * @returns {string} URL base formateada para el controlador Sequence.
 */
export const getApiBaseUrl = () => {
    const rawApiUrl = process.env.REACT_APP_API_URL || 'https://localhost:50586/api/';
    const normalizedUrl = rawApiUrl.endsWith('/') ? rawApiUrl.slice(0, -1) : rawApiUrl;
    return `${normalizedUrl}/Sequence`;
};

/**
 * Sanea y valida identificadores o valores de cabecera HTTP para prevenir inyecciones CRLF.
 * 
 * @param {string|number|null} val - Valor a sanear.
 * @returns {string} Valor saneado sin saltos de línea ni caracteres de control.
 */
const sanitizeHeaderValue = (val) => {
    if (val === null || val === undefined) return '';
    return String(val).replace(/[\r\n\t\0]/g, '').trim().substring(0, 100);
};

/**
 * Construye las cabeceras HTTP estándar para las peticiones JSON.
 * Adjunta X-Room-Id si se proporciona para aislamiento multi-tenant.
 * 
 * @param {string|number|null} [roomId=null] - Identificador único de la sala o pantalla.
 * @param {Object} [customHeaders={}] - Cabeceras personalizadas adicionales.
 * @returns {Object} Objeto de cabeceras listo para Fetch API.
 */
const buildHeaders = (roomId = null, customHeaders = {}) => {
    const headers = {
        'Content-Type': 'application/json',
        ...customHeaders
    };

    if (roomId !== null && roomId !== undefined) {
        const cleanRoomId = sanitizeHeaderValue(roomId);
        if (cleanRoomId) {
            headers['X-Room-Id'] = cleanRoomId;
        }
    }

    return headers;
};

// =========================================================================
// CONTROL DE PARTIDA Y TELEMETRÍA DE JUEGO
// =========================================================================

/**
 * Inicia una nueva partida de Secuencia con la configuración especificada.
 * Genera la secuencia aleatoria en el servidor y sincroniza a pantallas y aficionados.
 * 
 * @param {Object} configData - Parámetros de la partida.
 * @param {number|string} configData.screenId - Identificador de la pantalla asignada.
 * @param {number} [configData.sequenceLength=7] - Cantidad de pasos a generar (ej. 3 a 12).
 * @param {number} [configData.playbackSpeedMs=500] - Velocidad de reproducción en milisegundos (ej. 300ms a 1200ms).
 * @param {Array<Object>} [configData.buttons=null] - Configuración de botones/pads con colores e imágenes personalizadas.
 * @param {string|number|null} [roomId=null] - Identificador opcional de la sala para multi-tenant.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Resultado de la inicialización de la partida.
 */
export const StartSequenceMatch = async (configData, roomId = null) => {
    try {
        const effectiveRoomId = roomId || configData?.roomId || configData?.screenId || null;
        const requestUrl = `${getApiBaseUrl()}/Start`;

        const response = await fetch(requestUrl, {
            method: 'POST',
            headers: buildHeaders(effectiveRoomId),
            body: JSON.stringify(configData || {})
        });

        if (!response.ok) {
            const errorPayload = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${response.status} al iniciar la partida de Secuencia.`
            };
        }

        const responseData = await response.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Secuencia iniciada exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red o conexión al iniciar la dinámica Secuencia.'
        };
    }
};

/** Alias de compatibilidad para StartSequenceMatch */
export const StartSequenceGame = StartSequenceMatch;

/**
 * Registra el toque de un botón/color por parte del participante móvil o panel de administración.
 * Valida autoritativamente si el toque coincide con el paso esperado de la secuencia.
 * 
 * @param {number|string} screenId - Identificador de la pantalla donde se disputa la partida.
 * @param {string} matchId - Identificador GUID o UUID de la partida activa.
 * @param {number} colorId - Índice del pad/color pulsado (0 a 5).
 * @param {string|number|null} [roomId=null] - Identificador opcional de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Estado actualizado tras el toque.
 */
export const RegisterSequenceTap = async (screenId, matchId, colorId, roomId = null) => {
    try {
        const effectiveRoomId = roomId || screenId;
        const queryParams = new URLSearchParams({
            screenId: String(screenId),
            matchId: String(matchId),
            colorId: String(colorId)
        });

        const requestUrl = `${getApiBaseUrl()}/Tap?${queryParams.toString()}`;

        const response = await fetch(requestUrl, {
            method: 'POST',
            headers: buildHeaders(effectiveRoomId)
        });

        if (!response.ok) {
            const errorPayload = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${response.status} al registrar el toque.`
            };
        }

        const responseData = await response.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al registrar el toque en Secuencia.'
        };
    }
};

/**
 * Notifica al servidor que la pantalla gigante ha terminado de reproducir el destello de la secuencia.
 * Desbloquea la fase de interacción 'PLAYER_INPUT' para que el usuario pueda ingresar su respuesta.
 * 
 * @param {number|string} screenId - Identificador de la pantalla.
 * @param {string} matchId - Identificador único de la partida.
 * @param {string|number|null} [roomId=null] - Identificador opcional de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Confirmación de cambio de fase.
 */
export const FinishSequencePlayback = async (screenId, matchId, roomId = null) => {
    try {
        const effectiveRoomId = roomId || screenId;
        const queryParams = new URLSearchParams({
            screenId: String(screenId),
            matchId: String(matchId)
        });

        const requestUrl = `${getApiBaseUrl()}/FinishPlayback?${queryParams.toString()}`;

        const response = await fetch(requestUrl, {
            method: 'POST',
            headers: buildHeaders(effectiveRoomId)
        });

        if (!response.ok) {
            const errorPayload = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${response.status} al finalizar la reproducción.`
            };
        }

        const responseData = await response.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al notificar el fin de reproducción de Secuencia.'
        };
    }
};

/**
 * Reinicia la partida de Secuencia para una pantalla devolviéndola al estado 'WAITING'.
 * 
 * @param {number|string} screenId - Identificador de la pantalla a reiniciar.
 * @param {string|number|null} [roomId=null] - Identificador opcional de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Estado reiniciado.
 */
export const ResetSequenceMatch = async (screenId, roomId = null) => {
    try {
        const effectiveRoomId = roomId || screenId;
        const queryParams = new URLSearchParams({ screenId: String(screenId) });
        const requestUrl = `${getApiBaseUrl()}/Reset?${queryParams.toString()}`;

        const response = await fetch(requestUrl, {
            method: 'POST',
            headers: buildHeaders(effectiveRoomId)
        });

        if (!response.ok) {
            const errorPayload = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${response.status} al resetear la partida.`
            };
        }

        const responseData = await response.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Juego reiniciado exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al reiniciar la dinámica Secuencia.'
        };
    }
};

/** Alias de compatibilidad para ResetSequenceMatch */
export const ResetSequenceGame = ResetSequenceMatch;

/**
 * Obtiene el estado actual en memoria de la partida de Secuencia para una pantalla.
 * 
 * @param {number|string} screenId - Identificador de la pantalla.
 * @param {string|number|null} [roomId=null] - Identificador opcional de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Estado completo del juego.
 */
export const GetSequenceState = async (screenId, roomId = null) => {
    try {
        const effectiveRoomId = roomId || screenId;
        const queryParams = new URLSearchParams({ screenId: String(screenId) });
        const requestUrl = `${getApiBaseUrl()}/State?${queryParams.toString()}`;

        const response = await fetch(requestUrl, {
            method: 'GET',
            headers: buildHeaders(effectiveRoomId)
        });

        if (!response.ok) {
            const errorPayload = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${response.status} al consultar el estado de Secuencia.`
            };
        }

        const responseData = await response.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al consultar el estado de Secuencia.'
        };
    }
};

/**
 * Emite la orden a la pantalla gigante y aficionados para proyectar la portada principal de Secuencia.
 * 
 * @param {number|string} screenId - Identificador de la pantalla.
 * @param {string|number|null} [roomId=null] - Identificador opcional de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Confirmación de portada proyectada.
 */
export const ShowSequenceFrontPage = async (screenId, roomId = null) => {
    try {
        const effectiveRoomId = roomId || screenId;
        const queryParams = new URLSearchParams({ screenId: String(screenId) });
        const requestUrl = `${getApiBaseUrl()}/ShowFrontPage?${queryParams.toString()}`;

        const response = await fetch(requestUrl, {
            method: 'POST',
            headers: buildHeaders(effectiveRoomId)
        });

        if (!response.ok) {
            const errorPayload = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${response.status} al proyectar la portada.`
            };
        }

        const responseData = await response.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Portada proyectada exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al mostrar la portada de Secuencia.'
        };
    }
};

/**
 * Emite la orden a la pantalla gigante para proyectar el tablero de juego (los 6 pads) de Secuencia.
 * 
 * @param {number|string} screenId - Identificador de la pantalla.
 * @param {string|number|null} [roomId=null] - Identificador opcional de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Confirmación de proyección de juego.
 */
export const ProjectSequenceGame = async (screenId, roomId = null) => {
    try {
        const effectiveRoomId = roomId || screenId;
        const queryParams = new URLSearchParams({ screenId: String(screenId) });
        const requestUrl = `${getApiBaseUrl()}/ProjectGame?${queryParams.toString()}`;

        const response = await fetch(requestUrl, {
            method: 'POST',
            headers: buildHeaders(effectiveRoomId)
        });

        if (!response.ok) {
            const errorPayload = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${response.status} al proyectar el juego.`
            };
        }

        const responseData = await response.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Juego proyectado exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al proyectar el juego de Secuencia.'
        };
    }
};

// =========================================================================
// HISTORIAL DE PARTIDAS
// =========================================================================

/**
 * Obtiene el historial de partidas completadas de Secuencia.
 * 
 * @param {string|number|null} [roomId=null] - Identificador opcional de sala para filtrar.
 * @returns {Promise<{success: boolean, data?: Array<Object>, message?: string}>} Colección de partidas históricas.
 */
export const GetSequenceHistory = async (roomId = null) => {
    try {
        const queryParams = new URLSearchParams();
        if (roomId !== null && roomId !== undefined) {
            queryParams.append('roomId', String(roomId));
        }

        const queryString = queryParams.toString();
        const requestUrl = `${getApiBaseUrl()}/History${queryString ? `?${queryString}` : ''}`;

        const response = await fetch(requestUrl, {
            method: 'GET',
            headers: buildHeaders(roomId)
        });

        if (!response.ok) {
            const errorPayload = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${response.status} al obtener el historial.`
            };
        }

        const responseData = await response.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : (Array.isArray(responseData) ? responseData : [])
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al consultar el historial de Secuencia.'
        };
    }
};

/**
 * Elimina o limpia todo el historial de partidas de Secuencia.
 * 
 * @param {string|number|null} [roomId=null] - Identificador opcional de sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Confirmación de eliminación.
 */
export const ClearSequenceHistory = async (roomId = null) => {
    try {
        const queryParams = new URLSearchParams();
        if (roomId !== null && roomId !== undefined) {
            queryParams.append('roomId', String(roomId));
        }

        const queryString = queryParams.toString();
        const requestUrl = `${getApiBaseUrl()}/History${queryString ? `?${queryString}` : ''}`;

        const response = await fetch(requestUrl, {
            method: 'DELETE',
            headers: buildHeaders(roomId)
        });

        if (!response.ok) {
            const errorPayload = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${response.status} al limpiar el historial.`
            };
        }

        const responseData = await response.json().catch(() => ({}));
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Historial limpiado exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al limpiar el historial de Secuencia.'
        };
    }
};

// =========================================================================
// GESTIÓN DE TEMAS VISUALES Y BOTONES
// =========================================================================

/**
 * Obtiene el catálogo de temas visuales personalizados de Secuencia.
 * 
 * @param {string|number|null} [roomId=null] - Identificador opcional de sala.
 * @param {string|number|null} [operatorId=null] - Identificador opcional del operador.
 * @returns {Promise<{success: boolean, data?: Array<Object>, message?: string}>} Colección de temas visuales.
 */
export const GetSequenceThemes = async (roomId = null, operatorId = null) => {
    try {
        const queryParams = new URLSearchParams();
        if (roomId !== null && roomId !== undefined) queryParams.append('roomId', String(roomId));
        if (operatorId !== null && operatorId !== undefined) queryParams.append('operatorId', String(operatorId));

        const queryString = queryParams.toString();
        const requestUrl = `${getApiBaseUrl()}/GetThemes${queryString ? `?${queryString}` : ''}`;

        const response = await fetch(requestUrl, {
            method: 'GET',
            headers: buildHeaders(roomId)
        });

        if (!response.ok) {
            const errorPayload = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${response.status} al obtener los temas de Secuencia.`
            };
        }

        const responseData = await response.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : (Array.isArray(responseData) ? responseData : [])
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al consultar los temas de Secuencia.'
        };
    }
};

/**
 * Obtiene el detalle y botones configurados de un tema visual de Secuencia por su ID.
 * 
 * @param {number|string} id - Identificador único del tema.
 * @param {string|number|null} [roomId=null] - Identificador opcional de sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Detalle del tema consultado.
 */
export const GetSequenceThemeById = async (id, roomId = null) => {
    try {
        const sanitizedId = encodeURIComponent(String(id));
        const requestUrl = `${getApiBaseUrl()}/GetTheme/${sanitizedId}`;

        const response = await fetch(requestUrl, {
            method: 'GET',
            headers: buildHeaders(roomId)
        });

        if (!response.ok) {
            const errorPayload = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${response.status} al obtener el tema ${id}.`
            };
        }

        const responseData = await response.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData
        };
    } catch (networkError) {
        return {
            success: false,
            message: `Error de red al consultar el tema de Secuencia con ID ${id}.`
        };
    }
};

/** Alias de compatibilidad para GetSequenceThemeById */
export const GetSequenceTheme = GetSequenceThemeById;

/**
 * Guarda o actualiza un tema visual de Secuencia (creación si id=0, actualización si id>0).
 * 
 * @param {Object} theme - Datos del tema.
 * @param {number} [theme.id=0] - ID del tema (0 para crear nuevo).
 * @param {string} theme.name - Nombre identificador del tema o patrocinador.
 * @param {string|Array<Object>} theme.buttonsJson - JSON string o array con los 6 botones.
 * @param {string|number|null} [roomId=null] - Identificador opcional de sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Tema guardado y confirmado.
 */
export const SaveSequenceTheme = async (theme, roomId = null) => {
    try {
        const effectiveRoomId = roomId || theme?.roomId || null;
        const requestUrl = `${getApiBaseUrl()}/SaveTheme`;

        const response = await fetch(requestUrl, {
            method: 'POST',
            headers: buildHeaders(effectiveRoomId),
            body: JSON.stringify(theme)
        });

        if (!response.ok) {
            const errorPayload = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${response.status} al guardar el tema de Secuencia.`
            };
        }

        const responseData = await response.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Tema guardado exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al guardar el tema de Secuencia.'
        };
    }
};

/**
 * Elimina un tema visual personalizado de Secuencia.
 * 
 * @param {number|string} id - Identificador único del tema a eliminar.
 * @param {string|number|null} [roomId=null] - Identificador opcional de sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Confirmación de eliminación.
 */
export const DeleteSequenceTheme = async (id, roomId = null) => {
    try {
        const sanitizedId = encodeURIComponent(String(id));
        const requestUrl = `${getApiBaseUrl()}/DeleteTheme/${sanitizedId}`;

        const response = await fetch(requestUrl, {
            method: 'DELETE',
            headers: buildHeaders(roomId)
        });

        if (!response.ok) {
            const errorPayload = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${response.status} al eliminar el tema.`
            };
        }

        const responseData = await response.json().catch(() => ({}));
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Tema eliminado exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al eliminar el tema de Secuencia.'
        };
    }
};

// =========================================================================
// CONFIGURACIÓN DE PANTALLA
// =========================================================================

/**
 * Obtiene la configuración de Secuencia para una sala/pantalla específica (tema, velocidad, longitud).
 * 
 * @param {number|string} [screenId=1] - Identificador numérico de la pantalla.
 * @param {string|number|null} [roomId=null] - Identificador opcional de sala SaaS (GUID).
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Configuración activa.
 */
export const GetSequenceConfig = async (screenId = 1, roomId = null) => {
    try {
        const queryParams = [];
        if (roomId) queryParams.push(`roomId=${encodeURIComponent(String(roomId))}`);
        if (screenId) queryParams.push(`screenId=${encodeURIComponent(String(screenId))}`);
        const queryString = queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
        const requestUrl = `${getApiBaseUrl()}/GetConfig${queryString}`;

        const response = await fetch(requestUrl, {
            method: 'GET',
            headers: buildHeaders(roomId)
        });

        if (!response.ok) {
            const errorPayload = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${response.status} al obtener la configuración.`
            };
        }

        const responseData = await response.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al consultar la configuración de Secuencia.'
        };
    }
};

/**
 * Guarda o actualiza la configuración de parámetros de Secuencia para una sala/pantalla.
 * 
 * @param {Object} config - Parámetros de configuración.
 * @param {number|string} [config.screenId=1] - ID de la pantalla.
 * @param {number|null} [config.themeId=null] - ID del tema visual asignado.
 * @param {number} [config.sequenceLength=7] - Longitud de la secuencia.
 * @param {number} [config.playbackSpeedMs=500] - Velocidad en milisegundos.
 * @param {string|number|null} [roomId=null] - Identificador opcional de sala (GUID).
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Configuración persistida.
 */
export const SaveSequenceConfig = async (config, roomId = null) => {
    try {
        const effectiveRoomId = roomId || config?.roomId || null;
        const queryParam = effectiveRoomId ? `?roomId=${encodeURIComponent(String(effectiveRoomId))}` : '';
        const requestUrl = `${getApiBaseUrl()}/SaveConfig${queryParam}`;

        const payload = {
            roomId: effectiveRoomId && effectiveRoomId !== 'null' ? effectiveRoomId : '00000000-0000-0000-0000-000000000000',
            themeId: config?.themeId !== undefined ? config.themeId : null,
            sequenceLength: config?.sequenceLength || 7,
            playbackSpeedMs: config?.playbackSpeedMs || 500
        };

        const response = await fetch(requestUrl, {
            method: 'POST',
            headers: buildHeaders(effectiveRoomId),
            body: JSON.stringify(payload)
        });

        if (!response.ok) {
            const errorPayload = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${response.status} al guardar la configuración.`
            };
        }

        const responseData = await response.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Configuración guardada exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al guardar la configuración de Secuencia.'
        };
    }
};

// =========================================================================
// CARGA DE RECURSOS MULTIMEDIA
// =========================================================================

/**
 * Sube una imagen o recurso multimedia para personalizar los pads de Secuencia en formato multipart/form-data.
 * 
 * @param {File|Blob|FormData} fileOrFormData - Archivo binario o instancia FormData a transmitir.
 * @param {string|number|null} [roomId=null] - Identificador opcional de sala.
 * @returns {Promise<{success: boolean, url?: string, data?: Object, message?: string}>} URL pública del recurso almacenado.
 */
export const UploadSequenceMedia = async (fileOrFormData, roomId = null) => {
    try {
        let requestFormData = fileOrFormData;
        if (!(fileOrFormData instanceof FormData)) {
            requestFormData = new FormData();
            requestFormData.append('file', fileOrFormData);
        }

        const headers = {};
        if (roomId !== null && roomId !== undefined) {
            const cleanRoomId = sanitizeHeaderValue(roomId);
            if (cleanRoomId) {
                headers['X-Room-Id'] = cleanRoomId;
            }
        }

        const requestUrl = `${getApiBaseUrl()}/UploadMedia`;

        const response = await fetch(requestUrl, {
            method: 'POST',
            headers: headers,
            body: requestFormData
        });

        if (!response.ok) {
            const errorPayload = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${response.status} al subir el archivo multimedia.`
            };
        }

        const responseData = await response.json();
        const extractedUrl = responseData.url || responseData.data?.url || (typeof responseData === 'string' ? responseData : '');

        return {
            success: Boolean(responseData.success !== false),
            url: extractedUrl,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Recurso subido exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de conexión o red al subir el recurso multimedia de Secuencia.'
        };
    }
};

export default {
    StartSequenceMatch,
    StartSequenceGame,
    RegisterSequenceTap,
    FinishSequencePlayback,
    ResetSequenceMatch,
    ResetSequenceGame,
    GetSequenceState,
    ShowSequenceFrontPage,
    ProjectSequenceGame,
    GetSequenceHistory,
    ClearSequenceHistory,
    GetSequenceThemes,
    GetSequenceThemeById,
    GetSequenceTheme,
    SaveSequenceTheme,
    DeleteSequenceTheme,
    GetSequenceConfig,
    SaveSequenceConfig,
    UploadSequenceMedia,
    getApiBaseUrl
};
